import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import './MusicPlayer.css';

// --- LYRICS DATA & NEW SRT PARSER ---
const srtLyrics = `
1
00:00:09,000 --> 00:00:21,000
♪ Oh, oh, oh, oh, oh, oh ♪

2
00:00:21,000 --> 00:00:24,000
We are the dreamers, watch us rise

3
00:00:24,000 --> 00:00:26,000
Building a world before your eyes

4
00:00:26,000 --> 00:00:29,000
Every block, every line of code

5
00:00:29,000 --> 00:00:32,000
Is a new path, a different road

6
00:00:32,000 --> 00:00:37,000
We are New Foxes, wild and free

7
00:00:37,000 --> 00:00:43,000
In our world, we plant our tree

8
00:00:43,000 --> 00:00:47,000
No fake talk, just open hearts

9
00:00:47,000 --> 00:00:53,000
Together we build, a brand new start

10
00:00:53,000 --> 00:00:56,000
From the cafe's light to a screen so bright

11
00:00:56,000 --> 00:00:58,000
We connect all day and all through the night

12
00:00:58,000 --> 00:01:01,000
With no fear and no doubt, the future is ours

13
00:01:01,000 --> 00:01:04,000
We're reaching together, for the moon and the stars

14
00:01:04,000 --> 00:01:08,000
We are New Foxes, wild and free

15
00:01:08,000 --> 00:01:14,000
In our world, we plant our tree

16
00:01:14,000 --> 00:01:19,000
No fake talk, just open hearts

17
00:01:19,000 --> 00:01:24,000
Together we build, a brand new start

18
00:01:24,000 --> 00:01:35,000
♪ Oh, oh, oh, oh, oh, oh ♪

19
00:01:35,000 --> 00:01:37,000
Foxes don't beg, they build from scars

20
00:01:37,000 --> 00:01:40,000
We shine in the silence, we don't need stars

21
00:01:40,000 --> 00:01:43,000
Code in our blood, a purpose in our tone

22
00:01:43,000 --> 00:01:45,000
Even when lost, we still call it home

23
00:01:45,000 --> 00:01:48,000
Yeah, from the dirt to the chain, we leveled the scene

24
00:01:48,000 --> 00:01:51,000
While they flex online, we move unseen

25
00:01:51,000 --> 00:01:54,000
We're not noise, we're a signal, sharp and clean

26
00:01:54,000 --> 00:01:57,000
Real ones in code, you know what I mean

27
00:01:57,000 --> 00:01:59,000
Loyal to family, but never to fear

28
00:01:59,000 --> 00:02:02,000
Every loss we faced just made the mission clear

29
00:02:02,000 --> 00:02:05,000
We don't need crowns, we print our own name

30
00:02:05,000 --> 00:02:07,000
In hearts, in chains, in every single frame.

31
00:02:07,000 --> 00:02:12,000
WE ARE NEW FOXES, EYES ON THE SKY

32
00:02:12,000 --> 00:02:17,000
NEVER BACK DOWN, ONLY AIM HIGH

33
00:02:17,000 --> 00:02:20,000
NO MASK, NO LIE, NO FAKE APPLAUSE

34
00:02:20,000 --> 00:02:23,000
WE BUILD WITH TRUTH, AND WE BEND NO LAWS

35
00:02:23,000 --> 00:02:25,000
WILD AND FREE, FROM BLOCKS TO THE STREET

36
00:02:25,000 --> 00:02:28,000
EVERY DREAM WE HOLD IS THE GROUND BENEATH

37
00:02:28,000 --> 00:02:37,000
♪ Oh, oh, oh, oh ♪

38
00:02:37,000 --> 00:02:47,000
A brand new start

39
00:02:47,000 --> 00:02:52,000
With open hearts
`;

const parseSrtLyrics = (srt) => {
  const timeToSeconds = (timeStr) => {
    const [h, m, s] = timeStr.split(':');
    return parseInt(h, 10) * 3600 + parseInt(m, 10) * 60 + parseFloat(s.replace(',', '.'));
  };

  return srt.trim().split(/\n\s*\n/).map(entry => {
    const parts = entry.split('\n');
    if (parts.length < 3) return null;
    const timeMatch = parts[1].match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/);
    if (!timeMatch) return null;
    return {
      start: timeToSeconds(timeMatch[1]),
      end: timeToSeconds(timeMatch[2]),
      text: parts.slice(2).join(' '),
    };
  }).filter(Boolean);
};

const PlayIcon = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"></path></svg>;
const PauseIcon = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"></path></svg>;
const VolumeIcon = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"></path></svg>;

const MusicPlayer = ({ isVisible }) => {
  const audioRef = useRef(null);
  const volumeControlRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.4);
  const [isVolumeSliderVisible, setVolumeSliderVisible] = useState(false);

  const lyrics = useMemo(() => parseSrtLyrics(srtLyrics), []);
  const currentLyric = useMemo(() => lyrics.find(lyric => currentTime >= lyric.start && currentTime <= lyric.end)?.text || '\u00A0', [lyrics, currentTime]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.then(() => setIsPlaying(true)).catch((error) => {
        console.warn("Audio autoplay was prevented by the browser. User interaction is required to start audio.", error);
        setIsPlaying(false);
      });
    }

    const listeners = {
      timeupdate: () => setCurrentTime(audio.currentTime),
      play: () => setIsPlaying(true),
      pause: () => setIsPlaying(false),
    };
    Object.entries(listeners).forEach(([event, handler]) => audio.addEventListener(event, handler));
    return () => Object.entries(listeners).forEach(([event, handler]) => audio.removeEventListener(event, handler));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(event.target)) {
        setVolumeSliderVisible(false);
      }
    };
    if (isVolumeSliderVisible) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVolumeSliderVisible]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (audio.paused) audio.play(); else audio.pause();
  }, []);

  const toggleVolumeSlider = (e) => {
    e.stopPropagation();
    setVolumeSliderVisible(prev => !prev);
  };

  return (
    <>
      <audio ref={audioRef} src="/audio/newfoxes.mp3" preload="auto" loop autoPlay />
      <div className={`music-player-container ${isVisible ? 'visible' : 'hidden'}`}>
        <button onClick={togglePlayPause} className="play-pause-btn" aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
        <div className="lyrics-display">
          <p className="lyric-line">
            {isPlaying || currentTime > 0 ? currentLyric : "New Foxes - The Anthem"}
          </p>
        </div>
        <div ref={volumeControlRef} className="volume-control" onClick={toggleVolumeSlider}>
          <VolumeIcon />
          <div className={`volume-slider-wrapper ${isVolumeSliderVisible ? 'slider-visible' : ''}`}>
            <input 
              type="range" min="0" max="1" step="0.01" value={volume} 
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="volume-slider" aria-label="Volume"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default MusicPlayer;